import { Loader2, Sparkles, X } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { TabularPreviewTable } from '@/components/tabular/TabularPreviewTable'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import type { TabularColumn } from '@/services/api/lake'
import type { RowsPage } from '@/services/api/structuredIngest'

/** What marking rows to promote needs from the owner (state in, actions out). */
export interface RowsSelection {
  selected: Set<number>
  onToggle: (position: number) => void
  onReplace: (positions: number[]) => void
  onClear: () => void
  onPromote: (positions: number[], note: string) => void
  promoting: boolean
}

export interface RowsDialogProps {
  open: boolean
  title: string
  subtitle?: string | null
  page: RowsPage | null
  loading: boolean
  error?: string | null
  /** The search that led here — rows holding any of its words are marked and scrolled to. */
  highlight?: string | null
  /** When given, rows can be marked and promoted into the graph as one evidence node. */
  selection?: RowsSelection | null
  onOpenChange: (open: boolean) => void
  onPage: (offset: number) => void
}

/** The words worth matching in a search query: three letters or more, lower-cased. */
export function highlightTerms(query: string | null | undefined): string[] {
  return (query ?? '')
    .toLowerCase()
    .split(/[^\p{L}\p{N}@./:-]+/u)
    .filter(t => t.length >= 3)
}

function matches(value: string | undefined, terms: string[]): boolean {
  if (!value || terms.length === 0) return false
  const v = value.toLowerCase()
  return terms.some(t => v.includes(t))
}

const ROW_INDEX = '__row_index'

/** The verbatim page in the shared grid's shape: every column as text, `__row_index` last. */
function toTabular(page: RowsPage): { columns: TabularColumn[]; rows: (string | number)[][] } {
  const columns: TabularColumn[] = [
    ...page.columns.map(name => ({ name, data_type: 'string' })),
    { name: ROW_INDEX, data_type: 'integer' },
  ]
  const rows = page.rows.map(row => [...page.columns.map(c => row.values[c] ?? ''), row.position])
  return { columns, rows }
}

/** A phone leaves only a sliver of backdrop to tap, so the dialog gets a visible way out there. */
function PhoneClose() {
  const { t } = useTranslation()
  return (
    <DialogClose asChild>
      <Button
        variant="ghost"
        size="icon-sm"
        className="absolute top-2 right-2 md:hidden"
        aria-label={t('common.close')}
      >
        <X className="size-4" />
      </Button>
    </DialogClose>
  )
}

/**
 * The drilldown: verbatim rows of a structured table, exactly as the file had them
 * (every column, original spelling), paged. Used behind an aggregated edge, a
 * "heaviest pair" of the preview and a search hit on a row window. With a
 * `selection`, rows can be marked and promoted into the graph as evidence.
 */
export function RowsDialog({
  open,
  title,
  subtitle,
  page,
  loading,
  error,
  highlight,
  selection,
  onOpenChange,
  onPage,
}: RowsDialogProps) {
  const { t } = useTranslation()
  const [note, setNote] = useState('')
  const terms = useMemo(() => highlightTerms(highlight), [highlight])
  const hitRows = useMemo(
    () =>
      new Set(
        (page?.rows ?? [])
          .filter(r => Object.values(r.values).some(v => matches(v, terms)))
          .map(r => r.position)
      ),
    [page, terms]
  )
  const firstHit = useMemo(() => {
    const first = page?.rows.find(r => hitRows.has(r.position))
    return first ? first.position : null
  }, [page, hitRows])
  const table = useMemo(() => (page ? toTabular(page) : null), [page])
  const selected = selection?.selected ?? new Set<number>()
  const marked = [...selected].sort((a, b) => a - b)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[85dvh] w-[min(96vw,1400px)] max-w-[min(96vw,1400px)] flex-col gap-3 sm:max-w-[min(96vw,1400px)]">
        <PhoneClose />
        <DialogHeader className="max-md:pr-8">
          <DialogTitle className="text-base max-md:leading-snug max-md:break-words">
            {title}
          </DialogTitle>
          <p className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
            {subtitle && <span>{subtitle}</span>}
            {page && (
              <span>
                {t('tableMapping.rows.fileRows', {
                  file: page.filename,
                  rows: t('tableMapping.count.rows', { count: page.total }),
                })}
              </span>
            )}
            {page && terms.length > 0 && (
              <span className="text-foreground">
                {t('tableMapping.rows.matches', { count: hitRows.size, query: highlight })}
              </span>
            )}
            {selection && page && (
              <span className="text-foreground">{t('tableMapping.rows.markHint')}</span>
            )}
          </p>
        </DialogHeader>
        {error && <p className="text-sm text-destructive">{error}</p>}
        {loading && !page && (
          <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
            <Loader2 aria-hidden className="size-4 animate-spin motion-reduce:animate-none" />{' '}
            {t('tableMapping.rows.loading')}
          </div>
        )}
        {page && table && (
          <TabularPreviewTable
            className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-md border"
            columns={table.columns}
            rows={table.rows}
            currentOffset={page.offset}
            totalRows={page.total}
            isNavigating={loading}
            onPrevPage={() => onPage(Math.max(page.offset - page.limit, 0))}
            onNextPage={() => onPage(page.offset + page.limit)}
            headerRowIndex={null}
            pendingRows={selected}
            pendingColumns={new Set()}
            selectionMode={selection ? 'select-rows' : null}
            onRowClick={position => selection?.onToggle(position)}
            onColumnClick={() => {}}
            onDragSelect={selection ? positions => selection.onReplace(positions) : undefined}
            rowLabel={position => String(position + 1)}
            showColumnTypes={false}
            emphasis={{
              rows: hitRows,
              cell: (_row, _column, value) =>
                matches(typeof value === 'string' ? value : undefined, terms),
            }}
            scrollToRow={firstHit}
          />
        )}
        {selection && page && (
          <div className="flex flex-wrap items-center gap-2 border-t pt-3 text-xs">
            <span className="text-muted-foreground">
              {marked.length === 0
                ? t('tableMapping.rows.noneMarked')
                : t('tableMapping.rows.marked', { count: marked.length })}
            </span>
            <Input
              className="md:h-7 md:w-72"
              placeholder={t('tableMapping.rows.notePlaceholder')}
              value={note}
              onChange={e => setNote(e.target.value)}
              disabled={selection.promoting}
            />
            <Button
              size="sm"
              disabled={marked.length === 0 || selection.promoting}
              onClick={() => selection.onPromote(marked, note.trim())}
            >
              {selection.promoting ? (
                <Loader2 className="size-3.5 animate-spin" />
              ) : (
                <Sparkles className="size-3.5" />
              )}
              {marked.length > 0
                ? t('tableMapping.rows.promoteCount', { count: marked.length })
                : t('tableMapping.rows.promote')}
            </Button>
            {marked.length > 0 && (
              <Button
                size="sm"
                variant="ghost"
                onClick={selection.onClear}
                disabled={selection.promoting}
              >
                {t('tableMapping.rows.clear')}
              </Button>
            )}
            <span className="ml-auto text-muted-foreground">{t('tableMapping.rows.verbatim')}</span>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
